import { apiDelete, apiGet, apiPost, apiPut, type RequestOptions } from './index'

export interface ClockDay {
  id: number
  station_id: number
  template_id: number | null
  date: string
  window_start: string | null
  window_end: string | null
  title: string | null
  created_at: string
  created_by: number | null
  updated_at: string
  updated_by: number | null
}

export interface ClockDaySummary {
  id: number
  station_id: number
  title: string | null
  name: string | null
  window_start: string | null
  window_end: string | null
}

export interface ClockDayLookupResponse {
  now: string
  station_timezone: string
  current: ClockDaySummary | null
  next: ClockDaySummary | null
  seconds_until_next: number | null
  current_start_epoch?: number | null
  next_start_epoch?: number | null
  request_id?: string
}

export interface ClockDayItem {
  id: number
  day_id: number
  sort_order: number
  item_type: string
  title: string | null
  planned_duration_sec: number
  auto_advance: boolean
  fixed_time: string | null
  notes: string | null
  actual_start: string | null
  actual_start_iso_utc?: string | null
  actual_start_epoch?: number | null
  actual_end: string | null
  actual_end_iso_utc?: string | null
  actual_end_epoch?: number | null
  skipped: boolean
}

export interface ClockDayItemPrep {
  id: number
  day_item_id: number
  prep_item_id: number
  position: number
  added_at: string | null
  added_by: number | null
}

export interface ClockDayListResponse {
  days: ClockDay[]
  request_id?: string
}

export interface ClockDayDetailResponse {
  day: ClockDay
  items: ClockDayItem[]
  request_id?: string
}

import type { ClockLiveProjection } from './showClockLive'

export interface ClockDayItemsResponse {
  items: ClockDayItem[]
  projection?: ClockLiveProjection | null
  request_id?: string
}

export interface ClockDayItemResponse {
  item: ClockDayItem
  items?: ClockDayItem[]
  projection?: ClockLiveProjection | null
  request_id?: string
}

export interface ClockDayItemPrepsResponse {
  preps: ClockDayItemPrep[]
  request_id?: string
}

export interface ClockDayItemPrepResponse {
  prep: ClockDayItemPrep
  projection?: ClockLiveProjection | null
  request_id?: string
}

export interface CreateClockDayItemPayload {
  day_id: number
  item_type: string
  planned_duration_sec: number
  title?: string | null
  notes?: string | null
  fixed_time?: string | null
  after_item_id?: number | null
  auto_advance?: boolean | null
}

export interface CreateClockDayPayload {
  station_id: number
  template_id: number
  date: string
  window_start: string
  window_end: string
  title?: string | null
}

export interface UpdateClockDayPayload {
  title?: string | null
  window_start?: string | null
  window_end?: string | null
}

export interface UpdateClockDayItemPayload {
  title?: string | null
  planned_duration_sec?: number
  notes?: string | null
  fixed_time?: string | null
  auto_advance?: boolean | null
}

export interface ReorderPayload {
  order: Array<number | { id: number }>
}

export interface AttachPrepPayload {
  prep_item_id: number
}

export interface ReorderPrepsPayload {
  order: Array<number | { id: number }>
}

export const createClockDay = async (
  payload: CreateClockDayPayload,
  options?: RequestOptions,
): Promise<ClockDayDetailResponse> => {
  return apiPost<ClockDayDetailResponse>('clock/days', payload, options)
}

export const listClockDays = async (
  params: { stationId: number; date?: string },
  options?: RequestOptions,
): Promise<ClockDayListResponse> => {
  const search = new URLSearchParams({ station_id: String(params.stationId) })
  if (params.date) {
    search.set('date', params.date)
  }
  return apiGet<ClockDayListResponse>(`clock/days?${search.toString()}`, options)
}

export const lookupClockDay = async (
  params: { stationId: number; at?: string },
  options?: RequestOptions,
): Promise<ClockDayLookupResponse> => {
  const search = new URLSearchParams({ station_id: String(params.stationId) })
  if (params.at) {
    search.set('at', params.at)
  }
  return apiGet<ClockDayLookupResponse>(`clock/days/lookup?${search.toString()}`, options)
}

export const getClockDay = async (
  stationId: number,
  dayId: number,
  options?: RequestOptions,
): Promise<ClockDayDetailResponse> => {
  const search = new URLSearchParams({ station_id: String(stationId) })
  return apiGet<ClockDayDetailResponse>(`clock/days/${dayId}?${search.toString()}`, options)
}

export const updateClockDay = async (
  stationId: number,
  dayId: number,
  payload: UpdateClockDayPayload,
  options?: RequestOptions,
): Promise<ClockDayDetailResponse> => {
  const search = new URLSearchParams({ station_id: String(stationId) })
  return apiPut<ClockDayDetailResponse>(`clock/days/${dayId}?${search.toString()}`, payload, options)
}

export const deleteClockDay = async (
  stationId: number,
  dayId: number,
  options?: RequestOptions,
): Promise<void> => {
  const search = new URLSearchParams({ station_id: String(stationId) })
  await apiDelete<null>(`clock/days/${dayId}?${search.toString()}`, options)
}

export const listClockDayItems = async (
  stationId: number,
  dayId: number,
  options?: RequestOptions,
): Promise<ClockDayItemsResponse> => {
  const search = new URLSearchParams({ station_id: String(stationId) })
  return apiGet<ClockDayItemsResponse>(`clock/days/${dayId}/items?${search.toString()}`, options)
}

export const reorderClockDayItems = async (
  stationId: number,
  dayId: number,
  payload: ReorderPayload,
  options?: RequestOptions,
): Promise<ClockDayItemsResponse> => {
  const search = new URLSearchParams({ station_id: String(stationId) })
  return apiPost<ClockDayItemsResponse>(`clock/days/${dayId}/items?${search.toString()}`, payload, options)
}

export const createClockDayItem = async (
  stationId: number,
  payload: CreateClockDayItemPayload,
  options?: RequestOptions,
): Promise<ClockDayItemResponse> => {
  const search = new URLSearchParams({ station_id: String(stationId) })
  return apiPost<ClockDayItemResponse>(`clock/day-items?${search.toString()}`, payload, options)
}

export const updateClockDayItem = async (
  stationId: number,
  itemId: number,
  payload: UpdateClockDayItemPayload,
  options?: RequestOptions,
): Promise<ClockDayItemResponse> => {
  const search = new URLSearchParams({ station_id: String(stationId) })
  return apiPut<ClockDayItemResponse>(`clock/day-items/${itemId}?${search.toString()}`, payload, options)
}

export const deleteClockDayItem = async (
  stationId: number,
  itemId: number,
  options?: RequestOptions,
): Promise<ClockDayItemsResponse> => {
  const search = new URLSearchParams({ station_id: String(stationId) })
  return apiDelete<ClockDayItemsResponse>(`clock/day-items/${itemId}?${search.toString()}`, options)
}

export const listClockDayItemPreps = async (
  stationId: number,
  itemId: number,
  options?: RequestOptions,
): Promise<ClockDayItemPrepsResponse> => {
  const search = new URLSearchParams({ station_id: String(stationId) })
  return apiGet<ClockDayItemPrepsResponse>(`clock/day-items/${itemId}/preps?${search.toString()}`, options)
}

export const attachClockDayItemPrep = async (
  stationId: number,
  itemId: number,
  payload: AttachPrepPayload,
  options?: RequestOptions,
): Promise<ClockDayItemPrepResponse> => {
  const search = new URLSearchParams({ station_id: String(stationId) })
  return apiPost<ClockDayItemPrepResponse>(`clock/day-items/${itemId}/preps?${search.toString()}`, payload, options)
}

export const reorderClockDayItemPreps = async (
  stationId: number,
  itemId: number,
  payload: ReorderPrepsPayload,
  options?: RequestOptions,
): Promise<ClockDayItemPrepsResponse> => {
  const search = new URLSearchParams({ station_id: String(stationId) })
  return apiPut<ClockDayItemPrepsResponse>(`clock/day-items/${itemId}/preps?${search.toString()}`, payload, options)
}

export const detachClockDayItemPrep = async (
  stationId: number,
  prepId: number,
  options?: RequestOptions,
): Promise<void> => {
  const search = new URLSearchParams({ station_id: String(stationId) })
  await apiDelete<null>(`clock/day-item-preps/${prepId}?${search.toString()}`, options)
}

